import { useFragment } from "@apollo/client/react";
import { Dispatch, JSX, SetStateAction, useState } from "react";
import { useOutletContext, useParams } from "react-router-dom";
import { IntlShape, useIntl } from "react-intl";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import { PencilIcon, TrashIcon } from "lucide-react";

import { IndicatorNumber, TextInfoGroupAlt } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { CreateSiteVisitInput, SiteVisit, TenderDurationFragment, TenderDurationFragmentDoc } from "@/graphql/__generated__/graphql";
import { EditTenderContext } from "@/layouts/EditTenderLayout";
import { convertBpToDecimal, formatDateLocale, formatNumberLocale } from "@/utils/helpers";
import { useBreakpoint } from "@/hooks";
import { SubmitAction } from "@/constants/enums";
import { ConfirmRemoveAlert } from "../Dialog";
import { TSiteVisitSchema } from "../Models";
import { VisitAmountsDisplayPlaceholder, VisitAmountsDisplay } from "../Components";
import AddSiteVisitComponent from "./AddSiteVisitComponent";
import EditSiteVisitComponent from "./EditSiteVisitComponent";

const getDateRangeString = (t: TFunction,intl: IntlShape, siteVisit: SiteVisit | CreateSiteVisitInput, tenderDuration: TenderDurationFragment): string => {
  if (!siteVisit.startDate && !siteVisit.endDate) {
    return t("common:entirecontractduration");
  }

  const startDate = siteVisit.startDate ? formatDateLocale(intl, siteVisit.startDate) : formatDateLocale(intl, tenderDuration.contract.startDate);
  const endDate = siteVisit.endDate ? formatDateLocale(intl, siteVisit.endDate) : formatDateLocale(intl, tenderDuration.contract.endDate);
  return `${startDate} - ${endDate}`;
};

interface Props {
  siteVisitsToDisplay?: SiteVisit[]|CreateSiteVisitInput[];
  onAddSiteVisit: (_siteVisit: TSiteVisitSchema, _action: SubmitAction, _setShowSiteVisitsModal: Dispatch<SetStateAction<boolean>>) => Promise<void>;
  onRemoveSiteVisit: (_siteVisit: SiteVisit) => Promise<void>;
  onEditSiteVisit: (_siteVisit: TSiteVisitSchema, _selectedSiteVisit: SiteVisit, _setShowEditSiteVisitModal: Dispatch<SetStateAction<boolean>>) => Promise<void>;
  showRemoveSiteVisitAlert: boolean;
  setShowRemoveSiteVisitAlert: Dispatch<SetStateAction<boolean>>;
  editMode?: boolean;
  userCanEdit?: boolean;
  isProcessing?: boolean;
}

/**
 * Add Site Visit component. Acts as a wrapper for the AddSiteVisit form.
 *
 * @param {Props} props
 *
 * @returns {JSX.Element}
 */
const SiteVisitsComponent = (props: Props): JSX.Element => {
  // Props
  const {
    siteVisitsToDisplay,
    onAddSiteVisit,
    onRemoveSiteVisit,
    onEditSiteVisit,
    editMode = false,
    userCanEdit = false,
    isProcessing = false,
  } = props;

  // Hooks
  const intl = useIntl();
  const { t } = useTranslation(["translation", "common", "components"]);
  const { tenderID, serviceID } = useParams();
  const isLgBreakpoint = useBreakpoint("lg");
  const { isNewService } = useOutletContext<EditTenderContext>();

  // States
  const [showRemoveSiteVisitAlert, setShowRemoveSiteVisitAlert] = useState<boolean>(false);
  const [currentSiteVisit, setCurrentSiteVisit] = useState<SiteVisit | undefined>(undefined);
  const [showSiteVisitsModal, setShowSiteVisitsModal] = useState<boolean>(false);
  const [showEditSiteVisitModal, setShowEditSiteVisitModal] = useState<boolean>(false);

  // Get existing Tender contract duration from cache
  const { data: tenderDuration } = useFragment({
    fragment: TenderDurationFragmentDoc,
    from: {
      __typename: "Tender",
      id: tenderID,
    },
  }) as { data: TenderDurationFragment };

  /**
   * OnClick handler for the remove button in the Site Visits table
   * @param {SiteVisit} siteVisit - Current Site Visit object
   */
  const handleRemoveClick = (siteVisit: SiteVisit) => {
    setCurrentSiteVisit(siteVisit);
    setShowRemoveSiteVisitAlert(true);
  };

  const handleEditClick = (siteVisit: SiteVisit) => {
    setShowEditSiteVisitModal(true);
    setCurrentSiteVisit(siteVisit);
  };

  return (
    <>
      <div className="flex justify-between mb-2 items-center mt-2">
        <div className="flex items-center gap-2">
          <p className="font-semibold">{t("addsitevisit.title")}</p>
          <IndicatorNumber
            number={(siteVisitsToDisplay?.length ||0)}
          />
        </div>
        {
          (userCanEdit || isNewService) && (
            <Button
              onClick={() => setShowSiteVisitsModal(true)}
              variant="outline"
              className="ml-auto"
              disabled={isProcessing}
            >
              {t("addsitevisit.btnaddsitevisit")}
            </Button>
          )
        }
      </div>

      {/* Site Visits Modal */}
      {
        showSiteVisitsModal && (
          <AddSiteVisitComponent
            onAddSiteVisit={onAddSiteVisit}
            serviceID={Number(serviceID)}
            onOpen={showSiteVisitsModal}
            onOpenChange={setShowSiteVisitsModal}
            tenderDuration={tenderDuration}
            isProcessing={isProcessing}
          />
        )
      }

      {/* Edit Site Visit Modal */}
      {currentSiteVisit && showEditSiteVisitModal && (
        <EditSiteVisitComponent
          onEditSiteVisit={onEditSiteVisit}
          showSiteVisitModal={showEditSiteVisitModal}
          setShowSiteVisitModal={setShowEditSiteVisitModal}
          tenderDuration={tenderDuration}
          currentSiteVisit={currentSiteVisit as SiteVisit}
          isNewService={isNewService}
          isProcessing={isProcessing}
        />
      )}

      {/* Site Visit cards container */}
      <div className="flex-col-4">
        {siteVisitsToDisplay?.map((siteVisit: SiteVisit | CreateSiteVisitInput, index: number) => (
          // Site Visit card
          <Card className="p-4" key={`site-visit-${index}`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex flex-wrap md:items-center gap-4">
                {/* Type */}
                <TextInfoGroupAlt label={t("addsitevisit.type")} content={t("common:sitevisittype."+siteVisit.type.toLowerCase())} />
                {/* Margin */}
                <TextInfoGroupAlt label={t("addsitevisit.margin")} content={`${editMode ? convertBpToDecimal(siteVisit.margin) : siteVisit.margin}%`} />
                {/* Duration */}
                <TextInfoGroupAlt label={t("addsitevisit.duration")} content={`${siteVisit?.duration ?? ""} ${t("common:minutes").toLowerCase()}`} />
                {/* Date Range */}
                <TextInfoGroupAlt label={t("addsitevisit.daterange")} content={ getDateRangeString(t, intl, siteVisit, tenderDuration) } />
                {/* PricePerHour */}
                {
                  !isNewService && (
                    <TextInfoGroupAlt label={t("addsitevisit.pricepersitevisit")} content={formatNumberLocale(intl, (siteVisit as SiteVisit)?.pricePerSiteVisit ?? 0, "currency")} />
                  )
                }
              </div>
              {/* Action button */}
              {
                (userCanEdit || isNewService) && (
                  <div className="flex gap-2">
                    <Tooltip delayDuration={100}>
                      <TooltipTrigger asChild>
                        <Button
                          className="dark:text-white custom-button-icon"
                          variant="ghost"
                          onClick={() => handleEditClick(siteVisit as SiteVisit)}
                          disabled={isProcessing}
                        >
                          <PencilIcon className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{t("common:buttons.edit")}</p>
                      </TooltipContent>
                    </Tooltip>

                    <Tooltip delayDuration={100}>
                      <TooltipTrigger asChild>
                        <span>
                          <Button
                            className="dark:text-white custom-button-icon text-red-500 hover:text-red-500 hover:bg-red-500/10"
                            variant="ghost"
                            onClick={() => handleRemoveClick(siteVisit as SiteVisit)}
                            disabled={isProcessing || siteVisitsToDisplay.length === 1}
                          >
                            <TrashIcon className="h-4 w-4" />
                          </Button>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{siteVisitsToDisplay.length === 1 ? t("translation:addsitevisit.removelastsitevisit") : t("common:buttons.delete")}</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                )
              }
            </div>

            {/* Days */}
            <div className="lg:grid lg:grid-cols-4">
              {/* On mobile we only need one row of Day/Night labels */}
              { isLgBreakpoint ? Array.from({length:4}).map((_, index) => (
                <div key={index} className="my-2">
                  <VisitAmountsDisplayPlaceholder />
                </div>)
              ): ( <VisitAmountsDisplayPlaceholder />)}
              {["Monday", "Tuesday", "Wednesday", "Thursday",
                "Friday", "Saturday", "Sunday", "Holiday"].map((day) => (
                typeof siteVisit[`${day.toLowerCase()}Day`] === "number" &&
                typeof siteVisit[`${day.toLowerCase()}Night`] === "number" && (
                  <VisitAmountsDisplay
                    t={t}
                    key={day}
                    day={day}
                    dayValue={siteVisit[`${day.toLowerCase()}Day`] as number}
                    nightValue={siteVisit[`${day.toLowerCase()}Night`] as number}
                  />
                )
              ))}
            </div>
          </Card>
        ))}
      </div>

      {/* Remove Site Visit Alert Dialog */}
      <ConfirmRemoveAlert
        open={showRemoveSiteVisitAlert}
        onOpenChange={setShowRemoveSiteVisitAlert}
        actionButtonFn={() => {onRemoveSiteVisit(currentSiteVisit as SiteVisit); setShowRemoveSiteVisitAlert(false);}}
        bodyText={t("components:alertdialogs.removesitevisit")}
      />
    </>
  );
};

export default SiteVisitsComponent;
